import {
  FISHYMAP_CONTRACT_VERSION,
  FISHYMAP_POINT_ICON_SCALE_DEFAULT,
  FISHYMAP_POINT_ICON_SCALE_MIN,
} from "./map-bridge-contract.js";
import {
  EMPTY_SEARCH_EXPRESSION,
  normalizeRawSearchExpressionTree,
  resolveSearchExpression,
} from "./map-search-contract.js";
import { normalizeLootConditionContext } from "./map-loot-condition-context.js";
import {
  parseCalculatorLootRateDisplayMode,
} from "../js/components/calculator-loot-rate-presentation.js";
import {
  DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  normalizeVisibleTradeNpcClasses,
} from "./map-trade-npc-contract.js";

export const DEFAULT_ZONE_INFO_TAB = "";
export const DEFAULT_ZONE_LOOT_PROFILE = "rod";
export const DEFAULT_HOTSPOT_MODEL_TAB = "fish";
export const DEFAULT_AUTO_ADJUST_VIEW = true;
export const DEFAULT_SAMPLE_FROM_FILTERED_ELEMENTS = true;
export const DEFAULT_THREE_D_ENABLED = true;
export const DEFAULT_EFFECTS_ENABLED = true;
export const DEFAULT_FOCUS_WAVES_ENABLED = false;
export const DEFAULT_LOOT_RATE_DISPLAY_MODE = "normalized";
export const DEFAULT_ZONE_LOOT_SORT_BY = "calculator";
export const DEFAULT_ZONE_LOOT_SORT_ORDER = "calculator";
export const DEFAULT_ENABLED_LAYER_IDS = Object.freeze([
  "bookmarks",
  "fish_evidence",
  "zone_mask",
  "minimap",
]);
export const DEFAULT_LAYER_CLIP_MASKS = Object.freeze({
  fish_evidence: "zone_mask",
  regions: "zone_mask",
  region_groups: "zone_mask",
});

export const DEFAULT_WINDOW_UI_STATE = Object.freeze({
  search: Object.freeze({ open: true, collapsed: false, x: null, y: null }),
  help: Object.freeze({ open: true, collapsed: false, x: null, y: null }),
  settings: Object.freeze({
    open: false,
    collapsed: false,
    x: null,
    y: null,
    autoAdjustView: DEFAULT_AUTO_ADJUST_VIEW,
    sampleFromFilteredElements: DEFAULT_SAMPLE_FROM_FILTERED_ELEMENTS,
    threeDEnabled: DEFAULT_THREE_D_ENABLED,
    effectsEnabled: DEFAULT_EFFECTS_ENABLED,
    focusWavesEnabled: DEFAULT_FOCUS_WAVES_ENABLED,
    lootRateDisplayMode: DEFAULT_LOOT_RATE_DISPLAY_MODE,
    zoneLootSortBy: DEFAULT_ZONE_LOOT_SORT_BY,
    zoneLootSortOrder: DEFAULT_ZONE_LOOT_SORT_ORDER,
  }),
  zoneInfo: Object.freeze({
    open: true,
    collapsed: false,
    x: null,
    y: null,
    tab: DEFAULT_ZONE_INFO_TAB,
    lootProfile: DEFAULT_ZONE_LOOT_PROFILE,
    hotspotModelTab: DEFAULT_HOTSPOT_MODEL_TAB,
  }),
  layers: Object.freeze({ open: false, collapsed: false, x: null, y: null }),
  bookmarks: Object.freeze({ open: false, collapsed: false, x: null, y: null }),
});

export const DEFAULT_MAP_UI_SIGNAL_STATE = Object.freeze({
  windowUi: DEFAULT_WINDOW_UI_STATE,
  search: Object.freeze({
    open: false,
    expression: EMPTY_SEARCH_EXPRESSION,
    rawExpression: "",
    rawExpressionTree: null,
    expressionIssues: [],
  }),
  bookmarks: Object.freeze({ placing: false, selectedIds: [] }),
  layers: Object.freeze({
    expandedLayerIds: [],
    hoverFactsVisibleByLayer: {},
    sampleHoverVisibleByLayer: {},
    lineagePanelVisibleByLayer: {},
  }),
});

export const MAP_BRIDGE_SHARED_SIGNAL_WHITELIST = Object.freeze({
  bridged: Object.freeze({
    branch: "_map_bridged",
    filters: Object.freeze([
      "layerIdsVisible",
      "layerIdsOrdered",
      "layerFilterBindingIdsDisabledByLayer",
      "layerOpacities",
      "layerHighlights",
      "layerConfidenceShowcase",
      "layerClipMasks",
      "layerWaypointConnectionsVisible",
      "layerWaypointLabelsVisible",
      "layerPointIconsVisible",
      "layerPointIconScales",
      "layerPointClusteringEnabled",
      "tradeNpcClassesVisible",
      "filterOnlyActiveConditionGroups",
      "lootConditionSelection",
    ]),
    ui: Object.freeze([
      "showPoints",
      "showPointIcons",
      "viewMode",
      "pointIconScale",
      "bookmarkSelectedIds",
      "bookmarks",
    ]),
  }),
  session: Object.freeze({
    branch: "_map_session",
    fields: Object.freeze(["view", "selection"]),
  }),
  bookmarks: Object.freeze({
    branch: "_map_bookmarks",
    fields: Object.freeze(["entries"]),
  }),
  sharedFish: Object.freeze({
    branch: "_shared_fish",
    fields: Object.freeze(["caughtIds", "favouriteIds"]),
  }),
  actions: Object.freeze({
    branch: "_map_actions",
    fields: Object.freeze([
      "resetViewToken",
      "resetUiToken",
      "focusWorldPointToken",
      "focusWorldPoint",
    ]),
  }),
});

export const MAP_BRIDGE_SHARED_SIGNAL_BRANCHES = Object.freeze({
  bridged: MAP_BRIDGE_SHARED_SIGNAL_WHITELIST.bridged.branch,
  session: MAP_BRIDGE_SHARED_SIGNAL_WHITELIST.session.branch,
  bookmarks: MAP_BRIDGE_SHARED_SIGNAL_WHITELIST.bookmarks.branch,
  sharedFish: MAP_BRIDGE_SHARED_SIGNAL_WHITELIST.sharedFish.branch,
  actions: MAP_BRIDGE_SHARED_SIGNAL_WHITELIST.actions.branch,
});

export const DEFAULT_MAP_CONTROL_SIGNAL_STATE = Object.freeze({
  filters: Object.freeze({}),
  ui: Object.freeze({
    legendOpen: false,
    leftPanelOpen: true,
  }),
});

export const DEFAULT_MAP_BRIDGED_SIGNAL_STATE = Object.freeze({
  filters: Object.freeze({
    layerIdsVisible: DEFAULT_ENABLED_LAYER_IDS,
    layerIdsOrdered: [],
    layerFilterBindingIdsDisabledByLayer: {},
    layerOpacities: {},
    layerHighlights: {},
    layerConfidenceShowcase: {},
    layerClipMasks: DEFAULT_LAYER_CLIP_MASKS,
    layerWaypointConnectionsVisible: {},
    layerWaypointLabelsVisible: {},
    layerPointIconsVisible: {},
    layerPointIconScales: {},
    layerPointClusteringEnabled: {},
    tradeNpcClassesVisible: DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
    filterOnlyActiveConditionGroups: true,
    lootConditionSelection: {},
    lootConditionContext: {},
  }),
  ui: Object.freeze({
    showPoints: true,
    showPointIcons: true,
    viewMode: "2d",
    pointIconScale: FISHYMAP_POINT_ICON_SCALE_DEFAULT,
    bookmarkSelectedIds: [],
    bookmarks: [],
  }),
});

export const DEFAULT_MAP_BOOKMARKS_SIGNAL_STATE = Object.freeze({
  entries: [],
});

export const DEFAULT_MAP_SESSION_SIGNAL_STATE = Object.freeze({
  view: Object.freeze({
    viewMode: "2d",
    camera: {},
  }),
  selection: Object.freeze({}),
});

export const DEFAULT_MAP_ACTION_SIGNAL_STATE = Object.freeze({
  resetViewToken: 0,
  resetUiToken: 0,
  focusSearchToken: 0,
  focusWorldPointToken: 0,
  focusWorldPoint: Object.freeze({
    elementKind: "",
    worldX: null,
    worldZ: null,
    pointKind: "",
    pointLabel: "",
    historyBehavior: "append",
  }),
});

function cloneJsonValue(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwnKey(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function normalizeQueryIssueSpan(span) {
  const source = isPlainObject(span) ? span : {};
  const start = Number.parseInt(source.start, 10);
  const end = Number.parseInt(source.end, 10);
  return {
    start: Number.isInteger(start) && start >= 0 ? start : 0,
    end: Number.isInteger(end) && end >= 0 ? end : 0,
  };
}

function normalizeSearchExpressionIssues(issues) {
  if (!Array.isArray(issues)) {
    return [];
  }
  return issues.flatMap((issue) => {
    if (!isPlainObject(issue)) {
      return [];
    }
    const stage = String(issue.stage || "").trim();
    const code = String(issue.code || "").trim();
    if (!stage || !code) {
      return [];
    }
    const normalized = {
      stage,
      severity: issue.severity === "warning" ? "warning" : "error",
      code,
      span: normalizeQueryIssueSpan(issue.span),
      raw: String(issue.raw ?? ""),
      message: String(issue.message || ""),
    };
    if (Array.isArray(issue.expected)) {
      normalized.expected = issue.expected
        .map((value) => String(value ?? "").trim())
        .filter(Boolean);
    }
    return [normalized];
  });
}

function mergeDefaults(defaults, raw) {
  if (!isPlainObject(defaults)) {
    return raw === undefined ? defaults : raw;
  }
  const source = isPlainObject(raw) ? raw : {};
  const result = {};
  for (const [key, defaultValue] of Object.entries(defaults)) {
    if (!hasOwnKey(source, key)) {
      result[key] = cloneJsonValue(defaultValue);
      continue;
    }
    const rawValue = source[key];
    if (isPlainObject(defaultValue) && isPlainObject(rawValue)) {
      result[key] = mergeDefaults(defaultValue, rawValue);
      continue;
    }
    result[key] = cloneJsonValue(rawValue);
  }
  for (const [key, rawValue] of Object.entries(source)) {
    if (hasOwnKey(result, key)) {
      continue;
    }
    result[key] = cloneJsonValue(rawValue);
  }
  return result;
}

function normalizeBridgeBookmarkEntries(bookmarks) {
  if (!Array.isArray(bookmarks)) {
    return [];
  }
  return bookmarks.flatMap((bookmark) => {
    if (!isPlainObject(bookmark)) {
      return [];
    }
    const id = String(bookmark.id ?? "").trim();
    const worldX = Number(bookmark.worldX);
    const worldY = Number(bookmark.worldY);
    const worldZ = Number(bookmark.worldZ);
    if (
      !id
      || !Number.isFinite(worldX)
      || !Number.isFinite(worldY)
      || !Number.isFinite(worldZ)
    ) {
      return [];
    }
    const normalized = { id, worldX, worldY, worldZ };
    if (typeof bookmark.label === "string" && bookmark.label.trim()) {
      normalized.label = bookmark.label.trim();
    }
    return [normalized];
  });
}

export function normalizeZoneInfoTab(value) {
  return String(value || "").trim();
}

export function normalizeZoneLootProfile(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["rod", "harpoon", "unassigned"].includes(normalized) ? normalized : "rod";
}

function normalizeHotspotModelTab(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeZoneLootSortBy(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return [
    "price",
    "grade",
    "database",
    "evidence",
    "droprate",
    "expected_value",
    "exp",
    "totem_exp",
    "alphabetical",
    "item_id",
  ].includes(normalized)
    ? normalized
    : DEFAULT_ZONE_LOOT_SORT_BY;
}

function normalizeZoneLootSortOrder(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["desc", "asc"].includes(normalized)
    ? normalized
    : DEFAULT_ZONE_LOOT_SORT_ORDER;
}

export function normalizeWindowCoordinate(value) {
  if (value == null || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : null;
}

export function normalizeNullableString(value) {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

export function normalizeExpandedLayerIds(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

export function normalizeLayerStringListMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const next = {};
  for (const [layerIdRaw, bindingIdsRaw] of Object.entries(values)) {
    const layerId = String(layerIdRaw ?? "").trim();
    if (!layerId) {
      continue;
    }
    const bindingIds = normalizeExpandedLayerIds(bindingIdsRaw);
    if (!bindingIds.length) {
      continue;
    }
    next[layerId] = bindingIds;
  }
  return next;
}

function normalizeWindowUiEntry(rawEntry, fallbackEntry) {
  const baseEntry = isPlainObject(rawEntry) ? rawEntry : {};
  return {
    open: hasOwnKey(baseEntry, "open") ? baseEntry.open !== false : fallbackEntry.open !== false,
    collapsed: hasOwnKey(baseEntry, "collapsed")
      ? Boolean(baseEntry.collapsed)
      : Boolean(fallbackEntry.collapsed),
    x: hasOwnKey(baseEntry, "x") ? normalizeWindowCoordinate(baseEntry.x) : fallbackEntry.x,
    y: hasOwnKey(baseEntry, "y") ? normalizeWindowCoordinate(baseEntry.y) : fallbackEntry.y,
  };
}

function normalizeZoneInfoWindowUiEntry(rawEntry, fallbackEntry) {
  const baseEntry = isPlainObject(rawEntry) ? rawEntry : {};
  return {
    ...normalizeWindowUiEntry(baseEntry, fallbackEntry),
    tab: hasOwnKey(baseEntry, "tab")
      ? normalizeZoneInfoTab(baseEntry.tab)
      : normalizeZoneInfoTab(fallbackEntry?.tab),
    lootProfile: hasOwnKey(baseEntry, "lootProfile")
      ? normalizeZoneLootProfile(baseEntry.lootProfile)
      : normalizeZoneLootProfile(fallbackEntry?.lootProfile),
    hotspotModelTab: hasOwnKey(baseEntry, "hotspotModelTab")
      ? normalizeHotspotModelTab(baseEntry.hotspotModelTab)
      : normalizeHotspotModelTab(
          fallbackEntry?.hotspotModelTab || DEFAULT_HOTSPOT_MODEL_TAB,
        ),
  };
}

function normalizeSettingsWindowUiEntry(rawEntry, fallbackEntry) {
  const baseEntry = isPlainObject(rawEntry) ? rawEntry : {};
  let lootRateDisplayMode = DEFAULT_LOOT_RATE_DISPLAY_MODE;
  try {
    lootRateDisplayMode = parseCalculatorLootRateDisplayMode(
      hasOwnKey(baseEntry, "lootRateDisplayMode")
        ? baseEntry.lootRateDisplayMode
        : fallbackEntry?.lootRateDisplayMode ?? DEFAULT_LOOT_RATE_DISPLAY_MODE,
      "map settings lootRateDisplayMode",
    );
  } catch (_error) {
  }
  return {
    ...normalizeWindowUiEntry(baseEntry, fallbackEntry),
    autoAdjustView: hasOwnKey(baseEntry, "autoAdjustView")
      ? baseEntry.autoAdjustView !== false
      : fallbackEntry?.autoAdjustView !== false,
    sampleFromFilteredElements: hasOwnKey(baseEntry, "sampleFromFilteredElements")
      ? baseEntry.sampleFromFilteredElements !== false
      : fallbackEntry?.sampleFromFilteredElements !== false,
    threeDEnabled: hasOwnKey(baseEntry, "threeDEnabled")
      ? baseEntry.threeDEnabled !== false
      : fallbackEntry?.threeDEnabled !== false,
    effectsEnabled: hasOwnKey(baseEntry, "effectsEnabled")
      ? baseEntry.effectsEnabled !== false
      : fallbackEntry?.effectsEnabled !== false,
    focusWavesEnabled: hasOwnKey(baseEntry, "focusWavesEnabled")
      ? baseEntry.focusWavesEnabled === true
      : fallbackEntry?.focusWavesEnabled === true,
    lootRateDisplayMode,
    zoneLootSortBy: hasOwnKey(baseEntry, "zoneLootSortBy")
      ? normalizeZoneLootSortBy(baseEntry.zoneLootSortBy)
      : normalizeZoneLootSortBy(fallbackEntry?.zoneLootSortBy),
    zoneLootSortOrder: hasOwnKey(baseEntry, "zoneLootSortOrder")
      ? normalizeZoneLootSortOrder(baseEntry.zoneLootSortOrder)
      : normalizeZoneLootSortOrder(fallbackEntry?.zoneLootSortOrder),
  };
}

export function normalizeWindowUiState(rawState) {
  const source = isPlainObject(rawState) ? rawState : {};
  return {
    search: {
      ...normalizeWindowUiEntry(source.search, DEFAULT_WINDOW_UI_STATE.search),
      collapsed: false,
    },
    help: normalizeWindowUiEntry(source.help, DEFAULT_WINDOW_UI_STATE.help),
    settings: normalizeSettingsWindowUiEntry(source.settings, DEFAULT_WINDOW_UI_STATE.settings),
    zoneInfo: normalizeZoneInfoWindowUiEntry(source.zoneInfo, DEFAULT_WINDOW_UI_STATE.zoneInfo),
    layers: normalizeWindowUiEntry(source.layers, DEFAULT_WINDOW_UI_STATE.layers),
    bookmarks: normalizeWindowUiEntry(source.bookmarks, DEFAULT_WINDOW_UI_STATE.bookmarks),
  };
}

export function normalizeMapUiSignalState(raw) {
  const current = mergeDefaults(DEFAULT_MAP_UI_SIGNAL_STATE, raw);
  const rawSearch = isPlainObject(raw?.search) ? raw.search : {};
  const normalizedBookmarks = current?.bookmarks && typeof current.bookmarks === "object"
    ? current.bookmarks
    : {};
  const normalizedLayers = current?.layers && typeof current.layers === "object"
    ? current.layers
    : {};
  const searchExpression = resolveSearchExpression(
    hasOwnKey(rawSearch, "expression") ? rawSearch.expression : undefined,
  );
  return {
    windowUi: normalizeWindowUiState(current?.windowUi),
    search: {
      open: current?.search?.open === true,
      expression: searchExpression,
      rawExpression: String(current?.search?.rawExpression ?? ""),
      rawExpressionTree: normalizeRawSearchExpressionTree(current?.search?.rawExpressionTree),
      expressionIssues: normalizeSearchExpressionIssues(current?.search?.expressionIssues),
    },
    bookmarks: {
      placing: normalizedBookmarks.placing === true,
      selectedIds: Array.isArray(normalizedBookmarks.selectedIds)
        ? normalizedBookmarks.selectedIds
            .map((value) => String(value ?? "").trim())
            .filter(Boolean)
        : [],
    },
    layers: {
      expandedLayerIds: normalizeExpandedLayerIds(normalizedLayers.expandedLayerIds),
      hoverFactsVisibleByLayer: isPlainObject(normalizedLayers.hoverFactsVisibleByLayer)
        ? cloneJsonValue(normalizedLayers.hoverFactsVisibleByLayer)
        : {},
      sampleHoverVisibleByLayer: isPlainObject(normalizedLayers.sampleHoverVisibleByLayer)
        ? cloneJsonValue(normalizedLayers.sampleHoverVisibleByLayer)
        : {},
      lineagePanelVisibleByLayer: isPlainObject(normalizedLayers.lineagePanelVisibleByLayer)
        ? cloneJsonValue(normalizedLayers.lineagePanelVisibleByLayer)
        : {},
    },
  };
}

export function normalizeMapControlSignalState(raw) {
  const current = mergeDefaults(DEFAULT_MAP_CONTROL_SIGNAL_STATE, raw);
  return {
    filters: {},
    ui: {
      legendOpen: current.ui?.legendOpen === true,
      leftPanelOpen: current.ui?.leftPanelOpen !== false,
    },
  };
}

export function normalizeMapBridgedSignalState(raw) {
  const current = mergeDefaults(DEFAULT_MAP_BRIDGED_SIGNAL_STATE, raw);
  const rawFilters = isPlainObject(raw?.filters) ? raw.filters : null;
  const pointIconScale = Number(current.ui?.pointIconScale);
  return {
    version: FISHYMAP_CONTRACT_VERSION,
    filters: {
      layerIdsVisible: normalizeExpandedLayerIds(
        current.filters?.layerIdsVisible || DEFAULT_ENABLED_LAYER_IDS,
      ),
      layerIdsOrdered: normalizeExpandedLayerIds(current.filters?.layerIdsOrdered || []),
      layerFilterBindingIdsDisabledByLayer: normalizeLayerStringListMap(
        current.filters?.layerFilterBindingIdsDisabledByLayer || {},
      ),
      layerOpacities: cloneJsonValue(current.filters?.layerOpacities || {}),
      layerHighlights: cloneJsonValue(current.filters?.layerHighlights || {}),
      layerConfidenceShowcase: cloneJsonValue(
        current.filters?.layerConfidenceShowcase || {},
      ),
      layerClipMasks:
        rawFilters && hasOwnKey(rawFilters, "layerClipMasks")
          ? cloneJsonValue(rawFilters.layerClipMasks || {})
          : cloneJsonValue(current.filters?.layerClipMasks || {}),
      layerWaypointConnectionsVisible: cloneJsonValue(
        current.filters?.layerWaypointConnectionsVisible || {},
      ),
      layerWaypointLabelsVisible: cloneJsonValue(
        current.filters?.layerWaypointLabelsVisible || {},
      ),
      layerPointIconsVisible: cloneJsonValue(current.filters?.layerPointIconsVisible || {}),
      layerPointIconScales: cloneJsonValue(current.filters?.layerPointIconScales || {}),
      layerPointClusteringEnabled: cloneJsonValue(
        current.filters?.layerPointClusteringEnabled || {},
      ),
      tradeNpcClassesVisible: normalizeVisibleTradeNpcClasses(
        current.filters?.tradeNpcClassesVisible ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
      ),
      filterOnlyActiveConditionGroups:
        current.filters?.filterOnlyActiveConditionGroups !== false,
      lootConditionSelection: cloneJsonValue(current.filters?.lootConditionSelection || {}),
      lootConditionContext: normalizeLootConditionContext(current.filters?.lootConditionContext),
    },
    ui: {
      showPoints: current.ui?.showPoints !== false,
      showPointIcons: current.ui?.showPointIcons !== false,
      viewMode: current.ui?.viewMode === "3d" ? "3d" : normalizeNullableString(current.ui?.viewMode),
      pointIconScale: Number.isFinite(pointIconScale)
        ? pointIconScale
        : FISHYMAP_POINT_ICON_SCALE_DEFAULT,
      bookmarkSelectedIds: normalizeExpandedLayerIds(current.ui?.bookmarkSelectedIds || []),
      bookmarks: normalizeBridgeBookmarkEntries(current.ui?.bookmarks || []),
    },
  };
}
